import React, { useMemo, useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import '../lib/charts';
import { SERIES, seqColor, moneyTick } from '../lib/charts';
import useReport from '../lib/useReport';
import { useFilters } from '../contexts/FilterContext';
import { money, int, niceDate, WEEKDAYS } from '../lib/format';
import { Card, Spinner, ErrorBox, Empty } from '../components/ui';

// When did we sell? Trend over time + traffic by day-of-week and hour.
export default function TrendsPage() {
  const { reportParams, startDate, endDate } = useFilters();
  const [granularity, setGranularity] = useState('day');
  const [heatMetric, setHeatMetric] = useState('orders');

  const trend = useReport('sales-trend', { ...reportParams, granularity });
  const hourly = useReport('hourly-sales', reportParams);

  const heat = useMemo(() => {
    const cells = hourly.data?.cells || [];
    if (cells.length === 0) return null;
    const grid = Array.from({ length: 7 }, () => Array(24).fill(null));
    let max = 0;
    for (const c of cells) {
      const v = heatMetric === 'orders' ? c.orders : c.netSales;
      grid[c.weekday][c.hour] = c;
      if (v > max) max = v;
    }
    return { grid, max };
  }, [hourly.data, heatMetric]);

  // Day-of-week totals for the bar chart
  const dow = useMemo(() => {
    const cells = hourly.data?.cells || [];
    const totals = Array(7).fill(0);
    for (const c of cells) totals[c.weekday] += heatMetric === 'orders' ? c.orders : c.netSales;
    return totals;
  }, [hourly.data, heatMetric]);

  const trendChart = {
    labels: (trend.data || []).map(r =>
      granularity === 'month' ? r.bucket : niceDate(r.date)
    ),
    datasets: [{
      label: 'Net sales',
      data: (trend.data || []).map(r => r.netSales),
      borderColor: SERIES[0],
      backgroundColor: 'rgba(42,120,214,0.08)',
      fill: true,
      borderWidth: 2,
      pointRadius: (trend.data || []).length > 45 ? 0 : 2.5,
      tension: 0.25,
    }],
  };

  const gran = [['day', 'Daily'], ['week', 'Weekly'], ['month', 'Monthly']];
  const metric = [['orders', 'Orders'], ['netSales', 'Net sales']];

  return (
    <div className="grid" style={{ gap: 14 }}>
      <Card
        tour="trends-line"
        title="Net Sales Over Time"
        sub={`${niceDate(startDate)} – ${niceDate(endDate)} · business day starts 4 AM`}
        actions={
          <div className="chip-row">
            {gran.map(([id, label]) => (
              <button key={id} className={`chip ${granularity === id ? 'active' : ''}`} onClick={() => setGranularity(id)}>{label}</button>
            ))}
          </div>
        }
      >
        <div className="chart-box">
          {trend.loading ? <Spinner /> : trend.error ? <ErrorBox message={trend.error} /> : (
            <Line
              data={trendChart}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: ctx => ` ${money(ctx.parsed.y)} · ${int(trend.data[ctx.dataIndex]?.orders)} orders · tips ${money(trend.data[ctx.dataIndex]?.tips)}`,
                    },
                  },
                },
                scales: {
                  y: { ticks: { callback: moneyTick }, beginAtZero: true },
                  x: { grid: { display: false }, ticks: { maxTicksLimit: 12 } },
                },
              }}
            />
          )}
        </div>
      </Card>

      <div className="grid main-side">
        <Card
          tour="trends-heat"
          title="Busy Times"
          sub="volume by day of week and hour — darker is busier"
          actions={
            <div className="chip-row">
              {metric.map(([id, label]) => (
                <button key={id} className={`chip ${heatMetric === id ? 'active' : ''}`} onClick={() => setHeatMetric(id)}>{label}</button>
              ))}
            </div>
          }
        >
          {hourly.loading ? <Spinner /> : hourly.error ? <ErrorBox message={hourly.error} /> : !heat ? <Empty /> : (
            <div className="table-wrap">
              <table className="heatmap">
                <thead>
                  <tr>
                    <th />
                    {Array.from({ length: 24 }, (_, h) => (
                      <th key={h}>{h % 3 === 0 ? `${h}` : ''}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {WEEKDAYS.map((day, w) => (
                    <tr key={day}>
                      <td className="rowlabel">{day}</td>
                      {Array.from({ length: 24 }, (_, h) => {
                        const cell = heat.grid[w][h];
                        const v = cell ? (heatMetric === 'orders' ? cell.orders : cell.netSales) : 0;
                        return (
                          <td
                            key={h}
                            style={{ background: seqColor(v, heat.max) }}
                            title={cell ? `${day} ${h}:00 — ${int(cell.orders)} orders, ${money(cell.netSales)}` : `${day} ${h}:00 — no orders`}
                          >
                            {v ? (heatMetric === 'orders' ? v : '') : ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="legend-note">Hover any cell for exact orders and sales. Hours are local store time.</div>
            </div>
          )}
        </Card>

        <Card tour="trends-dow" title="By Day of Week" sub={heatMetric === 'orders' ? 'orders' : 'net sales'}>
          <div className="chart-box">
            <Bar
              data={{
                labels: WEEKDAYS,
                datasets: [{
                  data: dow,
                  backgroundColor: SERIES[0],
                  borderRadius: 4,
                  maxBarThickness: 30,
                }],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: ctx => heatMetric === 'orders' ? ` ${int(ctx.parsed.y)} orders` : ` ${money(ctx.parsed.y)}`,
                    },
                  },
                },
                scales: {
                  y: { beginAtZero: true, ticks: heatMetric === 'orders' ? {} : { callback: moneyTick } },
                  x: { grid: { display: false } },
                },
              }}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}
